import { sendEvent } from '@/utils/analytics';
import { EventType, CapitalStatus, NetworkType } from '@/types';
import { BigDecimal } from '@/utils/value-format';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAllMarketStoreStates } from './use-markets-store';
import {
  getAccountHealthPct,
  getAccountLeverage,
  getAccountMaintMarginReq,
  getAccountPnl,
  getAccountValue,
  getAvailableFunds,
  getPositionFunding,
  getPositionInitMargin,
  getPositionLiqPrice,
  getPositionUnrealizedPnL,
  getPositionValue,
  UnrealizedPnL,
} from '@/features/account/utils/math';
import { getFee } from '@/features/trade/order/utils/math';
import { useShallow } from 'zustand/react/shallow';
import { getDefaultBigDecimal } from '@/lib/utils';
import { getByNormalizedKey } from '@/utils/helpers';

export interface BasePosition {
  isLong: boolean;
  size: bigint;
  cost: bigint;
  entryFunding: bigint;
  initMarginRatio: bigint;
}

// All fields calculated
export interface Position extends BasePosition {
  liqPrice: BigDecimal;
  fee: BigDecimal;
  funding: BigDecimal;
  initMargin: BigDecimal;
  value: BigDecimal;
  unrealizedPnl: UnrealizedPnL;
}

export interface Positions {
  [symbol: string]: Position;
}

export type Leverage = {
  value: number;
  symbol: string;
};

export interface Account {
  lastUpdated: number;
  hasHydrated: boolean;
  collateral: bigint;
  positions: Positions;
  withdrawable: bigint;
  nonWithdrawable: bigint;
  connections: {
    twitter: string | null;
    discord: string | null;
  };
  // Calculated
  availableFunds: BigDecimal;
  value: BigDecimal;
  unrealizedPnl: BigDecimal;
  leverage: BigDecimal;
  maintMarginReq: BigDecimal;
  healthPct?: BigDecimal;
}

export interface AuthParams {
  address: string; // NOTE: currently checksummed, match wagmi; this is unused except during store rehydration
  signingKey: `0x${string}`;
  apiKey: string;
  accountGroup: number;
  listenKey: string;
  listenKeyExpiry: number;
  shouldPersist: boolean;
  // connector: string | undefined;
}

interface AccountStore {
  _hasHydrated: boolean; // internal zustand hydration
  setHasHydrated: (hasHydrated: boolean) => void;

  clearAccountStore: () => void;
  hasAuthHydrated: boolean; // api-provider auth hydration based on connected wallet
  hasPrimaryAuthHydrated: boolean;

  hasAuth: boolean;
  hasPrimaryAuth: boolean;
  setHasAuth: (hasAuth: boolean) => void;
  setHasPrimaryAuth: (hasPrimaryAuth: boolean) => void;

  fingerprintRequestId: string | undefined;
  setFingerprintRequestId: (requestId: string | undefined) => void;

  address: string | undefined;
  setAddress: (address: string | undefined) => void;

  authParamsByAddress: Record<string, AuthParams>; // store multiple authParams by address
  setAuthParams: (address: string, authParams: AuthParams) => void;
  getAuthParams: (
    address?: string,
    options?: { skipFallback?: boolean },
  ) => AuthParams | undefined;

  capitalStatusByAddress: Record<string, CapitalStatus>;
  getCapitalStatus: (address?: string) => CapitalStatus;
  setCapitalStatus: (status: CapitalStatus, address?: string) => void;

  timeOffset: number;
  networkType: NetworkType;
  setNetworkType: (networkType: NetworkType) => void;

  nonce: number;
  nextNonce: () => number; // returns new nonce

  accounts: Record<string, Account>;
  setAccount: (
    updater: Partial<Account>,
    lastUpdated: number,
    address?: string,
  ) => void;

  fundedModeActive: boolean;
  setFundedModeActive: (active: boolean) => void;
  fundedUpdateAddresses: string[];
  setFundedUpdateAddresses: (addresses: string[]) => void;

  getAccurateTime: () => number;

  updatePosition: (symbol: string, options?: { addresses?: string[] }) => void;
  updateAccount(options?: {
    address?: string;
    account?: Partial<Account>;
  }): void;
}

export const useAccountStore = create<AccountStore>()(
  persist(
    (set, get) => ({
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },
      networkType: NetworkType.EVM,
      setNetworkType: (networkType: NetworkType) => set({ networkType }),
      address: undefined,
      setAddress: (address: string | undefined) => {
        set((state) => ({
          address,
          // Reset hasHydrated on all accounts
          accounts: Object.entries(state.accounts).reduce(
            (prev, [address, account]) => ({
              ...prev,
              [address]: { ...account, hasHydrated: false },
            }),
            {} as Record<string, Account>,
          ),
        }));
        if (address) {
          sendEvent(EventType.LOG_IN, { address });
        }
      },

      timeOffset: 0,

      clearAccountStore() {
        set({
          address: undefined,
          hasAuth: false,
          authParamsByAddress: {},
          hasPrimaryAuth: false,
          accounts: {},
        });
      },

      hasAuthHydrated: false,
      hasPrimaryAuthHydrated: false,
      hasAuth: false,
      hasPrimaryAuth: false,
      setHasAuth: (hasAuth) => set({ hasAuth, hasAuthHydrated: true }),
      setHasPrimaryAuth: (hasPrimaryAuth) =>
        set({ hasPrimaryAuth, hasPrimaryAuthHydrated: true }),

      fingerprintRequestId: undefined,
      setFingerprintRequestId: (fingerprintRequestId) =>
        set({ fingerprintRequestId }),

      authParamsByAddress: {}, // Initialize as an empty object for multiple wallet support
      setAuthParams: (address, authParams) => {
        set((state) => {
          const updatedAuthParams = {
            ...state.authParamsByAddress,
            [address]: authParams,
          };
          return { authParamsByAddress: updatedAuthParams };
        });
      },

      getAuthParams: (address, options) => {
        if (!!options?.skipFallback && !address) {
          return;
        }

        const state = get();
        const resolvedAddress = address ?? state.address;

        return getByNormalizedKey(state.authParamsByAddress, resolvedAddress);
      },

      capitalStatusByAddress: {},
      getCapitalStatus: (address) => {
        if (!address) return CapitalStatus.UNKNOWN;
        address = address.toLowerCase();
        const { capitalStatusByAddress } = get();
        if (!(address in capitalStatusByAddress)) return CapitalStatus.UNKNOWN;
        return capitalStatusByAddress[address];
      },
      setCapitalStatus: (status, address) => {
        if (!address) return;
        address = address.toLowerCase();
        set((state) => ({
          capitalStatusByAddress: {
            ...state.capitalStatusByAddress,
            [address]: status,
          },
        }));
      },

      nonce: Date.now(),
      nextNonce: () => {
        const nonce = get().getAccurateTime();
        set({ nonce });
        return nonce;
      },
      getAccurateTime: () => Date.now() + get().timeOffset,

      accounts: {},
      setAccount: (account, lastUpdated, forcedAddress) => {
        const { address: storeAddress } = get();
        const address = forcedAddress ?? storeAddress;
        if (!address) return;
        set((state) => ({
          accounts: {
            ...state.accounts,
            [address]: {
              ...(state.accounts[address] ?? defaultAccount),
              ...account,
              lastUpdated,
            },
          },
        }));
      },

      fundedModeActive: false,
      setFundedModeActive: (active) => set({ fundedModeActive: active }),
      fundedUpdateAddresses: [],
      setFundedUpdateAddresses: (addresses) =>
        set({ fundedUpdateAddresses: addresses }),

      // processedFillsByAddress: {},
      // isFilledOrderProcessed: (
      //   orderId,
      //   lastFilledTime,
      //   lastFilledSize,
      //   addressOverride,
      // ) => {
      //   const address = (addressOverride ?? get().address)?.toLowerCase();

      //   if (!address) {
      //     return false;
      //   }

      //   const processedFills = get().processedFillsByAddress[address];

      //   if (!processedFills) {
      //     return false;
      //   }

      //   const processed = processedFills.get(orderId);

      //   if (!processed) {
      //     return false;
      //   }

      //   const isSameFill =
      //     processed.lastFilledTime === lastFilledTime &&
      //     processed.lastFilledSize === lastFilledSize;

      //   return isSameFill;
      // },
      // markFilledOrderAsProcessed: (
      //   orderId,
      //   lastFilledTime,
      //   lastFilledSize,
      //   addressOverride,
      // ) => {
      //   const address = (addressOverride ?? get().address)?.toLowerCase();

      //   if (!address) {
      //     return;
      //   }

      //   set((state) => {
      //     const processedFills =
      //       state.processedFillsByAddress[address] || new Map();

      //     processedFills.set(orderId, {
      //       orderId,
      //       lastFilledTime,
      //       lastFilledSize,
      //       processedAt: Date.now(),
      //     });

      //     return {
      //       processedFillsByAddress: {
      //         ...state.processedFillsByAddress,
      //         [address]: new Map(processedFills),
      //       },
      //     };
      //   });

      //   get().cleanupOldProcessedFills(address);
      // },

      // cleanupOldProcessedFills: (addressOverride, maxAgeMs = 60_000) => {
      //   const address = (addressOverride ?? get().address)?.toLowerCase();

      //   if (!address) {
      //     return;
      //   }

      //   const processedFills = get().processedFillsByAddress[address];

      //   if (!processedFills) {
      //     return;
      //   }

      //   const now = Date.now();
      //   let removedCount = 0;

      //   for (const [orderId, fill] of processedFills.entries()) {
      //     if (now - fill.processedAt > maxAgeMs) {
      //       processedFills.delete(orderId);
      //       removedCount++;
      //     }
      //   }

      //   if (removedCount > 0) {
      //     set((state) => ({
      //       processedFillsByAddress: {
      //         ...state.processedFillsByAddress,
      //         [address]: new Map(processedFills),
      //       },
      //     }));
      //   }
      // },

      updatePosition: (symbol, options) => {
        const { accounts, fundedModeActive, fundedUpdateAddresses, address } =
          get();
        const addressesToUpdate = options?.addresses
          ? options.addresses
          : fundedModeActive
            ? address
              ? [...fundedUpdateAddresses, address]
              : fundedUpdateAddresses
            : address
              ? [address]
              : [];

        addressesToUpdate.forEach((addr) => {
          const account = accounts[addr];
          if (!account || !(symbol in account.positions)) return;

          const markets = getAllMarketStoreStates();
          const position = account.positions[symbol];

          const update = (values: Partial<Position>) =>
            set((state) => {
              const currentAccount = state.accounts[addr];
              const currentPosition = currentAccount?.positions[symbol];
              if (!currentAccount || !currentPosition) return state;
              return {
                accounts: {
                  ...state.accounts,
                  [addr]: {
                    ...currentAccount,
                    positions: {
                      ...currentAccount.positions,
                      [symbol]: {
                        ...currentPosition,
                        ...values,
                      },
                    },
                  },
                },
              };
            });

          const {
            marketData: { midPrice },
          } = markets[symbol];

          const funding = getPositionFunding(
            symbol,
            position.isLong,
            position.size,
            position.entryFunding,
          );
          const fee = getFee(symbol, position.size, midPrice, {
            address: addr,
          });
          const initMargin = getPositionInitMargin(
            symbol,
            position.cost,
            position.initMarginRatio,
          );
          const value = getPositionValue(symbol, position.size);

          update({ funding, fee, initMargin, value });

          const unrealizedPnl = getPositionUnrealizedPnL(symbol, {
            isLong: position.isLong,
            size: position.size,
            cost: position.cost,
            initMargin,
          });

          update({ unrealizedPnl });

          get().updateAccount({ address: addr });

          const updatedAccount = get().accounts[addr];
          const liqPrice = updatedAccount
            ? getPositionLiqPrice(symbol, updatedAccount)
            : getPositionLiqPrice(symbol);

          update({ liqPrice });
        });
      },
      updateAccount(options) {
        const { accounts, address: storeAddress } = get();
        const address = options?.address ?? storeAddress;
        if (!address) return;

        const update = (values: Partial<Account>) =>
          set((state) => ({
            accounts: {
              ...state.accounts,
              [address]: {
                ...defaultAccount,
                ...(state.accounts[address] ?? {}),
                ...values,
              },
            },
          }));

        if (!options?.account) {
          const account = accounts[address];
          if (!account) return;

          const unrealizedPnl = getAccountPnl(account.positions);
          const maintMarginReq = getAccountMaintMarginReq(account.positions);

          update({ unrealizedPnl, maintMarginReq });

          const value = getAccountValue({
            collateral: account.collateral,
            unrealizedPnl,
          });

          update({ value });

          const leverage = getAccountLeverage({
            value,
            positions: account.positions,
          });
          const healthPct = getAccountHealthPct({ value, maintMarginReq });
          const availableFunds = getAvailableFunds({
            address,
            account: {
              collateral: account.collateral,
              positions: account.positions,
              unrealizedPnl,
            },
          });

          update({ availableFunds, healthPct, leverage });
        } else {
          update(options.account);
        }
      },
    }),
    {
      name: 'auth',
      partialize: (state) => {
        if (
          Object.values(state.authParamsByAddress).some(
            (param) => param.shouldPersist,
          )
        ) {
          return {
            capitalStatusByAddress: state.capitalStatusByAddress,
            authParamsByAddress: state.authParamsByAddress,
            networkType: state.networkType,
            address: state.address,
          };
        }

        return {
          capitalStatusByAddress: state.capitalStatusByAddress, // NOTE: this is public so we always persist it
        };
      },
      onRehydrateStorage: (state) => {
        return (state, error) => {
          if (error) {
            console.error('Error rehydrating account store', error);
            return;
          }
          if (!state) {
            console.error('State undefined after rehydration');
            return;
          }
          state.setHasHydrated(true);
        };
      },
    },
  ),
);

const defaultAccount: Account = {
  value: getDefaultBigDecimal(),
  unrealizedPnl: getDefaultBigDecimal(),
  leverage: { bigint: 100n, decimal: '1' },
  withdrawable: 0n,
  nonWithdrawable: 0n,
  availableFunds: getDefaultBigDecimal(),
  maintMarginReq: getDefaultBigDecimal(),
  collateral: 0n,
  positions: {},
  connections: {
    twitter: null,
    discord: null,
  },
  lastUpdated: 0,
  hasHydrated: false,
};

export const useCurrentAccount = <T = Account>(
  selector?: (account: Account) => T,
  address?: string,
): T =>
  useAccountStore(
    useShallow((state) => {
      // Get the current wallet address based on active wallet type
      const currentAddress = address ?? state.address;
      const account =
        getByNormalizedKey(state.accounts, currentAddress) ?? defaultAccount;
      return selector ? selector(account) : (account as T);
    }),
  );

export const getCurrentAccount = <T = Account>(
  selector?: (account: Account) => T,
  address?: string,
) => {
  const { address: storeAddress, accounts } = useAccountStore.getState();
  const resolvedAddress = address ?? storeAddress;
  const account =
    getByNormalizedKey(accounts, resolvedAddress) ?? defaultAccount;
  return selector ? selector(account) : (account as T);
};
